/**
 * Home Hero Product Section JavaScript
 * Handles image stack navigation, sticky behavior, and accessibility
 */

class HomeHeroProduct {
  constructor() {
    this.container = document.querySelector('.home-hero-product');
    if (!this.container) return;

    this.imageTrack = this.container.querySelector('[data-image-track]');
    this.imageDots = this.container.querySelectorAll('[data-image-dot]');
    this.purchaseOptions = this.container.querySelectorAll('.purchase-option__button');
    this.primaryCTA = this.container.querySelector('#primary-cta');
    this.productForm = this.container.querySelector('form[data-home-hero-product-form]');

    this.currentImageIndex = 0;
    this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.selectedVariantId = null;
    this.selectedSellingPlanId = '';
    this.selectedBundleVariantId = null;
    this.selectedDiscountCode = '';
    this.selectedPurchaseType = '';

    this.init();
  }

  init() {
    console.log('[HomeHeroProduct] Initializing...', {
      hasContainer: !!this.container,
      hasForm: !!this.productForm,
      hasCTA: !!this.primaryCTA,
      purchaseOptionCount: this.purchaseOptions.length
    });

    this.setupImageStack();
    this.setupPurchaseOptions();
    this.setupFormSubmitInterceptor();
    this.setupPrimaryCTA();
    this.setupAccessibility();
    this.setupResizeObserver();

    // Fetch variant IDs for options where product picker didn't resolve (page template)
    this.fetchVariantIdsForEmptyOptions().then(() => {
      this.autoSelectFirstOption();
    });
  }

  async fetchVariantIdsForEmptyOptions() {
    if (!this.purchaseOptions.length) return;
    const toFetch = [];
    const toFetchGift = [];
    this.purchaseOptions.forEach((btn) => {
      const variantId = (btn.dataset.variantId || '').trim();
      const handle = (btn.dataset.productHandle || '').trim();
      if (!variantId && handle) toFetch.push({ btn, handle });
      const purchaseType = btn.dataset.purchaseType || '';
      const bundleVariantId = (btn.dataset.bundleVariantId || '').trim();
      const giftHandle = (btn.dataset.giftProductHandle || '').trim();
      const isMultiItem = ['subscription_plus_gift', 'one_time_plus_gift', 'bundle'].includes(purchaseType);
      
      if (isMultiItem && !bundleVariantId && giftHandle) {
        toFetchGift.push({ btn, handle: giftHandle });
      }
    });
    await Promise.all([
      ...toFetch.map(({ btn, handle }) =>
        fetch(`/products/${handle}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .then((product) => {
            if (product?.variants?.[0]?.id) {
              btn.dataset.variantId = String(product.variants[0].id);
            }
          })
          .catch(() => { })
      ),
      ...toFetchGift.map(({ btn, handle }) =>
        fetch(`/products/${handle}.js`)
          .then((r) => (r.ok ? r.json() : null))
          .then((product) => {
            if (product?.variants?.[0]?.id) {
              btn.dataset.bundleVariantId = String(product.variants[0].id);
            }
          })
          .catch(() => { })
      )
    ]);
  }

  autoSelectFirstOption() {
    if (this.purchaseOptions.length > 0) {
      // Log all purchase option state for debugging
      console.log('[HomeHeroProduct] Purchase options found:', this.purchaseOptions.length);
      this.purchaseOptions.forEach((btn, i) => {
        console.log(`[HomeHeroProduct] Option ${i}:`, {
          label: btn.querySelector('.purchase-option__label')?.textContent?.trim(),
          variantId: btn.dataset.variantId,
          sellingPlanId: btn.dataset.sellingPlanId,
          purchaseType: btn.dataset.purchaseType,
          productHandle: btn.dataset.productHandle,
          isPrimary: btn.classList.contains('primary')
        });
      });

      // Find the first primary option or default to the first one
      let firstOption = this.purchaseOptions[0];

      for (let option of this.purchaseOptions) {
        if (option.classList.contains('primary') || option.dataset.isPrimary === 'true') {
          firstOption = option;
          break;
        }
      }

      // Simulate click on the first option
      setTimeout(() => {
        console.log('[HomeHeroProduct] Auto-selecting option:', firstOption.querySelector('.purchase-option__label')?.textContent?.trim());
        firstOption.click();
      }, 100);
    } else {
      console.warn('[HomeHeroProduct] No purchase options found in DOM');
    }
  }

  setupImageStack() {
    if (!this.imageTrack || !this.imageDots.length) return;

    // Set initial active dot
    this.updateActiveDot(0);

    // Handle scroll events for image stack
    this.imageTrack.addEventListener('scroll', this.handleImageScroll.bind(this));

    // Handle dot navigation clicks
    this.imageDots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToImage(index));
    });

    // Handle keyboard navigation for dots
    this.imageDots.forEach((dot, index) => {
      dot.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.goToImage(index);
        }
      });
    });
  }

  setupPurchaseOptions() {
    if (!this.purchaseOptions.length) return;

    this.purchaseOptions.forEach(option => {
      option.addEventListener('click', this.handlePurchaseOptionClick.bind(this));

      // Handle keyboard navigation
      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handlePurchaseOptionClick(e);
        }
      });
    });
  }

  setupFormSubmitInterceptor() {
    if (!this.productForm) return;
    this.productForm.addEventListener('submit', (e) => {
      const idInput = this.productForm.querySelector('input[name="id"]');
      const variantId = idInput ? idInput.value : '';

      if (!variantId || variantId.trim() === '') {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.showErrorMessage('Please select a purchase option first. If you’ve already done so, configure the Product for each option in the theme editor (Home Hero Product → Purchase options).');
        return;
      }

      if (this.selectedPurchaseType === 'bundle' && this.selectedVariantId && this.selectedBundleVariantId) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.setAddToCartLoading(true);
        (async () => {
          await this.cartAdd(this.selectedVariantId, 1, '');
          const response = await this.cartAdd(this.selectedBundleVariantId, 1, '');
          const parsedState = await this.ensureCartSections(response);
          this.openCartDrawer(parsedState);
        })()
          .catch(err => this.showErrorMessage(err.message || 'Could not add bundle to basket.'))
          .finally(() => this.setAddToCartLoading(false));
      }
      if ((this.selectedPurchaseType === 'subscription_plus_gift' || this.selectedPurchaseType === 'one_time_plus_gift') && this.selectedVariantId && this.selectedBundleVariantId) {
        e.preventDefault();
        e.stopImmediatePropagation();
        this.setAddToCartLoading(true);
        (async () => {
          const sellingPlanId = this.selectedPurchaseType === 'subscription_plus_gift' ? (this.selectedSellingPlanId || '').trim() : '';
          await this.cartAdd(this.selectedVariantId, 1, sellingPlanId);
          const response = await this.cartAdd(this.selectedBundleVariantId, 1, '');
          if ((this.selectedPurchaseType === 'subscription_plus_gift' || this.selectedPurchaseType === 'one_time_plus_gift') && window.celfLinkedTracker) window.celfLinkedTracker.push(this.selectedVariantId);
          const parsedState = await this.ensureCartSections(response);
          this.openCartDrawer(parsedState);
        })()
          .catch(err => this.showErrorMessage(err.message || 'Could not add to basket. Please try again.'))
          .finally(() => this.setAddToCartLoading(false));
      }
    }, true);
  }

  setAddToCartLoading(loading) {
    if (!this.primaryCTA) return;
    if (loading) {
      this.primaryCTA.setAttribute('aria-disabled', 'true');
      this.primaryCTA.disabled = true;
      this.primaryCTA.classList.add('is-loading');
    } else {
      this.primaryCTA.removeAttribute('aria-disabled');
      this.primaryCTA.disabled = false;
      this.primaryCTA.classList.remove('is-loading');
    }
  }

  setupPrimaryCTA() {
    if (!this.primaryCTA) return;

    // When we have a product form, Add to Basket is the submit button – form handles one_time/subscription
    if (this.productForm) {
      this.disablePrimaryCTA();
      return;
    }

    // Standalone (page) mode: primary CTA is a button that triggers JS add-to-cart
    this.disablePrimaryCTA();
    this.primaryCTA.addEventListener('click', (e) => {
      e.preventDefault();
      this.addToCart();
    });
    this.primaryCTA.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.addToCart();
      }
    });
    this.primaryCTA.setAttribute('title', 'Please select a purchase option first');
  }

  setupAccessibility() {
    // Ensure proper ARIA labels and roles
    if (this.imageDots.length > 1) {
      this.imageDots.forEach((dot, index) => {
        dot.setAttribute('aria-label', `Go to image ${index + 1} of ${this.imageDots.length}`);
        dot.setAttribute('role', 'button');
        dot.setAttribute('tabindex', '0');
      });
    }

    // Set up accordion accessibility
    const accordionItems = this.container.querySelectorAll('.accordion-item');
    accordionItems.forEach(item => {
      const trigger = item.querySelector('.accordion-trigger');
      const content = item.querySelector('.accordion-content');

      if (trigger && content) {
        trigger.setAttribute('aria-expanded', 'false');
        content.setAttribute('aria-hidden', 'true');

        item.addEventListener('toggle', () => {
          const isOpen = item.hasAttribute('open');
          trigger.setAttribute('aria-expanded', isOpen.toString());
          content.setAttribute('aria-hidden', (!isOpen).toString());
        });
      }
    });
  }

  setupResizeObserver() {
    // Handle responsive behavior changes
    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver(() => {
        this.updateStickyOffset();
      });

      if (this.container) {
        resizeObserver.observe(this.container);
      }
    }
  }

  handleImageScroll() {
    if (!this.imageTrack || this.isReducedMotion) return;

    // Detect scroll direction based on container dimensions
    const isHorizontalScroll = this.imageTrack.scrollWidth > this.imageTrack.clientWidth;

    let currentIndex;
    if (isHorizontalScroll) {
      // Horizontal scrolling (mobile)
      const scrollLeft = this.imageTrack.scrollLeft;
      const itemWidth = this.imageTrack.scrollWidth / this.imageDots.length;
      currentIndex = Math.round(scrollLeft / itemWidth);
    } else {
      // Vertical scrolling (desktop)
      const scrollTop = this.imageTrack.scrollTop;
      const itemHeight = this.imageTrack.scrollHeight / this.imageDots.length;
      currentIndex = Math.round(scrollTop / itemHeight);
    }

    if (currentIndex !== this.currentImageIndex) {
      this.currentImageIndex = currentIndex;
      this.updateActiveDot(currentIndex);
    }
  }

  goToImage(index) {
    if (!this.imageTrack || index < 0 || index >= this.imageDots.length) return;

    // Detect scroll direction based on container dimensions
    const isHorizontalScroll = this.imageTrack.scrollWidth > this.imageTrack.clientWidth;

    if (isHorizontalScroll) {
      // Horizontal scrolling (mobile)
      const itemWidth = this.imageTrack.scrollWidth / this.imageDots.length;
      const targetScrollLeft = index * itemWidth;

      if (this.isReducedMotion) {
        this.imageTrack.scrollLeft = targetScrollLeft;
      } else {
        this.imageTrack.scrollTo({
          left: targetScrollLeft,
          behavior: 'smooth'
        });
      }
    } else {
      // Vertical scrolling (desktop)
      const itemHeight = this.imageTrack.scrollHeight / this.imageDots.length;
      const targetScrollTop = index * itemHeight;

      if (this.isReducedMotion) {
        this.imageTrack.scrollTop = targetScrollTop;
      } else {
        this.imageTrack.scrollTo({
          top: targetScrollTop,
          behavior: 'smooth'
        });
      }
    }

    this.currentImageIndex = index;
    this.updateActiveDot(index);
  }

  updateActiveDot(index) {
    this.imageDots.forEach((dot, i) => {
      if (i === index) {
        dot.classList.add('active');
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.classList.remove('active');
        dot.removeAttribute('aria-current');
      }
    });
  }

  handlePurchaseOptionClick(event) {
    const button = event.currentTarget;
    const purchaseType = (button.dataset.purchaseType || 'one_time').trim();
    const variantId = (button.dataset.variantId || '').trim();
    const sellingPlanId = (button.dataset.sellingPlanId || '').trim();
    const bundleVariantId = (button.dataset.bundleVariantId || '').trim();
    const discountCode = (button.dataset.discountCode || '').trim();

    this.selectedVariantId = variantId;
    this.selectedSellingPlanId = sellingPlanId;
    this.selectedBundleVariantId = bundleVariantId;
    this.selectedDiscountCode = discountCode;
    this.selectedPurchaseType = purchaseType;

    this.updatePrimaryCTA(button);
    this.updateFormForSelectedOption();
    this.updatePurchaseOptionStates(button);

    const canAdd = variantId && (
      purchaseType === 'one_time' ||
      (purchaseType === 'subscription' && sellingPlanId) ||
      (purchaseType === 'bundle' && bundleVariantId) ||
      (purchaseType === 'subscription_plus_gift' && bundleVariantId) ||
      (purchaseType === 'one_time_plus_gift' && bundleVariantId)
    );

    console.log('[HomeHeroProduct] Option clicked:', {
      purchaseType, variantId, sellingPlanId, bundleVariantId, discountCode, canAdd,
      hasForm: !!this.productForm, hasCTA: !!this.primaryCTA
    });

    if (canAdd) this.enablePrimaryCTA();
    else this.disablePrimaryCTA();
  }

  updateFormForSelectedOption() {
    if (!this.productForm || !this.selectedVariantId) return;
    const idInput = this.productForm.querySelector('input[name="id"]');
    const bundleIdInput = this.productForm.querySelector('[data-bundle-variant-id-input]');
    const sellingPlanInput = this.productForm.querySelector('[data-selling-plan-input]');
    if (idInput) idInput.value = this.selectedVariantId;
    if (bundleIdInput) bundleIdInput.value = this.selectedBundleVariantId || '';
    if (sellingPlanInput) sellingPlanInput.value = (this.selectedPurchaseType === 'subscription' || this.selectedPurchaseType === 'subscription_plus_gift') ? this.selectedSellingPlanId : '';
  }

  updatePrimaryCTA(selectedButton) {
    if (!this.primaryCTA) return;

    const price = selectedButton.querySelector('.purchase-option__price')?.textContent;
    const textEl = this.primaryCTA.querySelector('.celf-atb__text');

    if (price && textEl) {
      textEl.textContent = `Add to Basket - ${price.trim()}`;
      this.primaryCTA.setAttribute('aria-describedby', 'selected-option');
    }

    // Reset any transient states
    this.primaryCTA.classList.remove('is-loading', 'is-success', 'is-error');

    this.primaryCTA.dataset.selectedPurchaseType = this.selectedPurchaseType;
    this.primaryCTA.dataset.selectedVariantId = this.selectedVariantId;
    this.primaryCTA.dataset.selectedSellingPlanId = this.selectedSellingPlanId;
    this.primaryCTA.dataset.selectedBundleVariantId = this.selectedBundleVariantId || '';
    this.primaryCTA.dataset.selectedDiscountCode = this.selectedDiscountCode || '';
  }

  enablePrimaryCTA() {
    if (this.primaryCTA) {
      this.primaryCTA.disabled = false;
      this.primaryCTA.classList.remove('disabled');
      this.primaryCTA.setAttribute('aria-disabled', 'false');
      this.primaryCTA.removeAttribute('title');
    }
  }

  disablePrimaryCTA() {
    if (this.primaryCTA) {
      this.primaryCTA.disabled = true;
      this.primaryCTA.classList.add('disabled');
      this.primaryCTA.setAttribute('aria-disabled', 'true');
      this.primaryCTA.setAttribute('title', 'Please select a purchase option first');
    }
  }

  updatePurchaseOptionStates(selectedButton) {
    this.purchaseOptions.forEach(button => {
      button.classList.remove('primary');
      button.setAttribute('aria-pressed', 'false');
    });

    selectedButton.classList.add('primary');
    selectedButton.setAttribute('aria-pressed', 'true');
  }

  // ---------------------------------------------------------------------------
  // Cart integration helpers
  // ---------------------------------------------------------------------------

  /** Locate the theme's cart-drawer or cart-notification element (cached). */
  getCartElement() {
    if (this._cartEl === undefined) {
      this._cartEl = document.querySelector('cart-drawer') || document.querySelector('cart-notification') || null;
    }
    return this._cartEl;
  }

  /** Section IDs the cart-drawer needs for a live update. */
  getCartSectionIds() {
    const cart = this.getCartElement();
    if (cart && typeof cart.getSectionsToRender === 'function') {
      return cart.getSectionsToRender().map((s) => s.id);
    }
    return ['cart-drawer', 'cart-icon-bubble'];
  }

  /**
   * Core fetch wrapper that talks to /cart/add.js the same way the theme's
   * product-form.js does, including requesting updated section HTML so the
   * cart-drawer can refresh itself.
   */
  async cartAdd(variantId, quantity = 1, sellingPlanId = '') {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', quantity);
    if (sellingPlanId) formData.append('selling_plan', sellingPlanId);

    // Ask Shopify to include rendered cart sections in the response
    const sectionIds = this.getCartSectionIds();
    formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', window.location.pathname);

    const cartAddUrl = (window.routes && window.routes.cart_add_url) || '/cart/add.js';

    console.log('[HomeHeroProduct] cartAdd →', { variantId, quantity, sellingPlanId, cartAddUrl });

    const res = await fetch(cartAddUrl, {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/javascript'
      },
      body: formData
    });

    const body = await res.json().catch(() => null);
    console.log('[HomeHeroProduct] cartAdd response', res.status, body);

    if (!res.ok || (body && body.status)) {
      const msg = (body && (body.description || body.message)) || `HTTP ${res.status}`;
      throw new Error(msg);
    }

    return body;
  }

  /**
   * Redirect to checkout with a discount code applied (e.g. for subscription+ gift flow).
   */
  redirectToCheckoutWithDiscount(discountCode) {
    const checkoutUrl = (window.routes && window.routes.checkout_url) || '/checkout';
    const url = discountCode ? `${checkoutUrl}?discount=${encodeURIComponent(discountCode)}` : checkoutUrl;
    window.location.href = url;
  }

  /**
   * After a successful cartAdd, hand the response to the cart-drawer so it
   * re-renders itself and slides open.  If the theme has no drawer, redirect
   * to the cart page.
   */
  openCartDrawer(response) {
    const cart = this.getCartElement();
    if (cart && typeof cart.renderContents === 'function') {
      console.log('[HomeHeroProduct] Opening cart drawer');
      cart.renderContents(response);
      return;
    }
    // No cart drawer — redirect to cart page
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    console.log('[HomeHeroProduct] No cart drawer, redirecting to', cartUrl);
    window.location = cartUrl;
  }

  /**
   * Fetch fresh cart sections via Section Rendering API. Use this when the
   * cart/add response may have null or stale sections (e.g. when adding the
   * first item or doing sequential adds like subscription_plus_gift).
   */
  async fetchCartSections() {
    const sectionIds = this.getCartSectionIds();
    const base = (typeof window.Shopify !== 'undefined' && window.Shopify.routes?.root) || window.location.pathname || '/';
    const url = `${base}?sections=${sectionIds.join(',')}`;
    const res = await fetch(url);
    const sections = await res.json().catch(() => ({}));
    return sections;
  }

  /**
   * Ensure we have valid cart sections for the drawer. When adding the first
   * item or doing sequential adds, cart/add may return null or stale sections.
   * Fetch fresh sections via Section Rendering API using current page URL.
   */
  async ensureCartSections(response) {
    const sections = response?.sections || {};
    const cartDrawerHtml = sections['cart-drawer'];
    const hasValidDrawer = cartDrawerHtml && typeof cartDrawerHtml === 'string' && cartDrawerHtml.length > 100;

    let finalSections = sections;
    if (!hasValidDrawer) {
      const freshSections = await this.fetchCartSections();
      finalSections = { ...sections, ...freshSections };
    }

    return {
      id: response?.id || response?.items?.[0]?.id || response?.key,
      item_count: response?.item_count ?? (response?.items?.length > 0 ? 1 : 0),
      sections: finalSections
    };
  }

  // ---------------------------------------------------------------------------
  // Add-to-cart entry point (standalone / no product form)
  // ---------------------------------------------------------------------------

  async addToCart() {
    if (!this.primaryCTA || this.primaryCTA.disabled) return;

    const variantId = this.primaryCTA.dataset.selectedVariantId || this.selectedVariantId;
    const purchaseType = this.primaryCTA.dataset.selectedPurchaseType || this.selectedPurchaseType;

    if (!variantId) {
      this.showErrorMessage('Please select a purchase option first.');
      return;
    }

    console.log('[HomeHeroProduct] addToCart', { variantId, purchaseType });

    // Enter loading state
    this.primaryCTA.disabled = true;
    this.primaryCTA.classList.remove('is-success', 'is-error');
    this.primaryCTA.classList.add('is-loading');

    try {
      let response;

      if (purchaseType === 'bundle') {
        const bundleVariantId = this.primaryCTA.dataset.selectedBundleVariantId || this.selectedBundleVariantId;
        if (!bundleVariantId) throw new Error('Bundle product is required for this option.');
        await this.cartAdd(variantId, 1, '');
        response = await this.cartAdd(bundleVariantId, 1, '');
        this.primaryCTA.classList.remove('is-loading');
        this.primaryCTA.classList.add('is-success');
        const parsedState = await this.ensureCartSections(response);
        this.openCartDrawer(parsedState);
        return;
      } else if (purchaseType === 'subscription_plus_gift' || purchaseType === 'one_time_plus_gift') {
        const bundleVariantId = this.primaryCTA.dataset.selectedBundleVariantId || this.selectedBundleVariantId;
        const sellingPlanId = purchaseType === 'subscription_plus_gift' ? (this.primaryCTA.dataset.selectedSellingPlanId || this.selectedSellingPlanId || '').trim() : '';
        if (!bundleVariantId) throw new Error('Main product and gift product are required for this option.');
        await this.cartAdd(variantId, 1, sellingPlanId || '');
        response = await this.cartAdd(bundleVariantId, 1, '');
        if ((this.selectedPurchaseType === 'subscription_plus_gift' || this.selectedPurchaseType === 'one_time_plus_gift') && window.celfLinkedTracker) window.celfLinkedTracker.push(variantId);
        this.primaryCTA.classList.remove('is-loading');
        this.primaryCTA.classList.add('is-success');
        const parsedState = await this.ensureCartSections(response);
        this.openCartDrawer(parsedState);
        return;
      } else if (purchaseType === 'subscription') {
        const sellingPlanId = this.primaryCTA.dataset.selectedSellingPlanId || this.selectedSellingPlanId;
        if (!sellingPlanId) throw new Error('Selling plan ID is required for subscriptions.');
        response = await this.cartAdd(variantId, 1, sellingPlanId);
      } else {
        response = await this.cartAdd(variantId, 1, '');
      }

      // Success — show tick, then open cart drawer
      this.primaryCTA.classList.remove('is-loading');
      this.primaryCTA.classList.add('is-success');

      const parsedState = await this.ensureCartSections(response);
      this.openCartDrawer(parsedState);

    } catch (error) {
      console.error('[HomeHeroProduct] addToCart error:', error);
      this.showErrorMessage(error.message || 'Error adding product to basket. Please try again.');
      this.primaryCTA.classList.remove('is-loading');
      this.primaryCTA.classList.add('is-error');
    } finally {
      // Reset button back to its normal state after a short delay
      setTimeout(() => {
        this.primaryCTA.disabled = false;
        this.primaryCTA.classList.remove('is-loading', 'is-success', 'is-error');
        const selected = this.container?.querySelector('.purchase-option__button.primary');
        if (selected) this.updatePrimaryCTA(selected);
      }, 2500);
    }
  }

  // ---------------------------------------------------------------------------
  // Notifications
  // ---------------------------------------------------------------------------

  showSuccessMessage(message = 'Product added to basket!') {
    this._showNotification(message, '#2e7d32', '\u2713');
  }

  showErrorMessage(message = 'Error adding product to basket. Please try again.') {
    this._showNotification(message, '#c62828', '\u2717');
  }

  _showNotification(message, bgColor, icon) {
    const el = document.createElement('div');
    el.setAttribute('style',
      `position:fixed;top:20px;right:20px;z-index:99999;padding:14px 22px;
       border-radius:8px;color:#fff;font-size:15px;font-family:sans-serif;
       background:${bgColor};box-shadow:0 4px 12px rgba(0,0,0,.25);
       display:flex;align-items:center;gap:8px;opacity:0;
       transition:opacity .3s ease;`
    );
    el.innerHTML = `<span style="font-size:18px">${icon}</span><span>${message}</span>`;
    document.body.appendChild(el);
    requestAnimationFrame(() => { el.style.opacity = '1'; });
    setTimeout(() => {
      el.style.opacity = '0';
      setTimeout(() => el.remove(), 400);
    }, 4000);
  }

  updateStickyOffset() {
    // Calculate sticky offset based on header height
    const header = document.querySelector('header');
    if (header) {
      const headerHeight = header.offsetHeight;
      document.documentElement.style.setProperty('--sticky-offset', `${headerHeight}px`);
    }
  }

  // Public method to programmatically select a purchase option
  selectPurchaseOption(index) {
    if (this.purchaseOptions[index]) {
      this.purchaseOptions[index].click();
    }
  }

  // Public method to go to a specific image
  goToImageByIndex(index) {
    this.goToImage(index);
  }

  // Cleanup method
  destroy() {
    // Remove event listeners if needed
    if (this.imageTrack) {
      this.imageTrack.removeEventListener('scroll', this.handleImageScroll);
    }
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new HomeHeroProduct();
});

// Export for potential external use
if (typeof module !== 'undefined' && module.exports) {
  module.exports = HomeHeroProduct;
}
